// src/features/dashboard/components/KPICard.tsx
import { Users, TrendingUp, Wallet, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { useCountUp } from '../hooks/useCountUp';
import { formatStatValue } from '../utils/formatters';
import type { TrendDirection } from '../types/dashboard.types';

export type KPIIconKey = 'sales' | 'customers' | 'wallet';

const ICON_MAP: Record<KPIIconKey, typeof Users> = {
  customers: Users,
  sales: TrendingUp,
  wallet: Wallet,
};

const TREND_ICON = { up: ArrowUp, down: ArrowDown, flat: Minus } as const;

export interface KPICardTrend {
  direction: TrendDirection;
  percent: number;
}

interface KPICardProps {
  icon: KPIIconKey;
  label: string;
  value: number;
  format: 'number' | 'currency' | 'percent';
  subtitle?: string;
  trend?: KPICardTrend;
  accent: 'violet' | 'teal' | 'amber' | 'rose' | 'sky';
  /** stagger index used to offset the entrance animation */
  index?: number;
}

/**
 * Premium KPI card for the Hero section of the Representative Dashboard.
 * Built on the same glass surface / accent / trend classes as the existing
 * StatCard (dashboard.css) so there is no duplicated CSS, plus a top
 * gradient accent bar unique to the hero KPI row. Entrance and hover
 * motion are handled entirely by existing CSS (the shared `[data-animate]`
 * fade-in keyframe and `.vania-glass-card--interactive` hover lift), both
 * of which already respect `prefers-reduced-motion` -- no JS animation
 * library involved.
 *
 * Values are rendered exactly as received via props -- no mock, derived,
 * or placeholder data is generated inside this component.
 */
export function KPICard({ icon, label, value, format, subtitle, trend, accent, index = 0 }: KPICardProps) {
  const Icon = ICON_MAP[icon];
  const animatedValue = useCountUp(value);

  const TrendIcon = trend ? TREND_ICON[trend.direction] : null;
  const trendToneClass = trend
    ? trend.direction === 'up'
      ? 'vania-trend--up'
      : trend.direction === 'down'
        ? 'vania-trend--down'
        : 'vania-trend--flat'
    : '';

  return (
    <div
      className={`vania-glass-card vania-glass-card--interactive vania-kpi-card vania-kpi-card--${accent} p-5 sm:p-6`}
      style={{ animationDelay: `${index * 60}ms` }}
      data-animate="fade-in"
    >
      <div className="flex items-start justify-between gap-3 min-w-0">
        <span className={`vania-icon-badge vania-icon-badge--${accent}`}>
          <Icon size={19} strokeWidth={2.2} />
        </span>
        {trend && TrendIcon && (
          <span className={`vania-trend shrink-0 ${trendToneClass}`}>
            <TrendIcon size={12} strokeWidth={2.5} />
            {trend.percent > 0 ? `${trend.percent}٪` : '—'}
          </span>
        )}
      </div>

      <div className="mt-5 flex flex-col gap-1.5">
        <span className="vania-stat-value text-2xl sm:text-[1.75rem] font-bold tabular-nums leading-none text-[var(--vania-text-primary,#f5f6fa)]">
          {formatStatValue(animatedValue, format)}
        </span>
        <span className="vania-stat-label text-sm font-medium text-[var(--vania-text-secondary,#a3aab8)]">
          {label}
        </span>
      </div>

      {subtitle && (
        <div className="mt-4 pt-3 border-t border-[var(--vania-border-glass,rgba(255,255,255,0.08))]">
          <span className="text-xs text-[var(--vania-text-tertiary,#6b7280)]">{subtitle}</span>
        </div>
      )}
    </div>
  );
}
